<template>
  <section class="tool-page page-shell">
    <div class="section-heading">
      <button class="back-link" @click="navigate('home')">← Home</button>
      <span class="eyebrow"><span></span>For the little things</span>
      <h1>Let chance <em>clear the noise.</em></h1>
      <p>Low stakes deserve low effort. Choose a tool and move on.</p>
    </div>

    <div class="segmented">
      <button v-for="item in tools" :key="item.id" :class="{ active: tool === item.id }" @click="tool = item.id">{{ item.icon }} {{ item.label }}</button>
    </div>

    <div v-if="tool === 'wheel'" class="tool-panel wheel-layout">
      <div class="option-editor">
        <span class="step-label">ADD YOUR OPTIONS</span>
        <h2>What's competing for your attention?</h2>
        <div v-for="(option, index) in options" :key="index" class="option-row">
          <span :style="{ background: colors[index % colors.length] }"></span>
          <input v-model.trim="options[index]" :placeholder="`Option ${index + 1}`" maxlength="42">
          <button v-if="options.length > 2" @click="options.splice(index, 1)">×</button>
        </div>
        <button v-if="options.length < 8" class="add-button" @click="options.push('')">＋ Add another option</button>
      </div>

      <div class="wheel-stage">
        <div class="wheel-pointer"></div>
        <div class="wheel" :class="{ spinning }" :style="wheelStyle">
          <span v-for="(option, index) in validOptions" :key="index" :style="labelStyle(index)">{{ option }}</span>
          <i>calm<br>decide</i>
        </div>
        <button class="primary-button" :disabled="spinning || validOptions.length < 2" @click="spin">{{ spinning ? 'Letting go…' : 'Spin the wheel' }}</button>
      </div>
    </div>

    <div v-else-if="tool === 'coin'" class="tool-panel centered-tool">
      <span class="step-label">HEADS OR TAILS</span>
      <h2>Give both sides a meaning.</h2>
      <div class="coin-inputs"><input v-model="coinHeads" maxlength="32"><span>or</span><input v-model="coinTails" maxlength="32"></div>
      <button class="coin" :class="{ flipping: coinFlipping }" @click="flipCoin"><span>{{ coinFace === 'heads' ? 'H' : 'T' }}</span></button>
      <strong v-if="coinResult" class="coin-result">{{ coinResult }}</strong>
      <button class="primary-button" :disabled="coinFlipping" @click="flipCoin">Flip the coin</button>
    </div>

    <div v-else class="tool-panel centered-tool scratch-tool">
      <span class="step-label">ONE CLEAR SUGGESTION</span>
      <h2>Borrow an idea. Keep it or swipe.</h2>
      <div class="idea-card" :class="{ changing: cardChanging }">
        <span>{{ currentIdea.category }}</span><i>{{ currentIdea.icon }}</i><h3>{{ currentIdea.title }}</h3><p>{{ currentIdea.detail }}</p>
      </div>
      <div class="card-actions"><button @click="nextIdea">Not this one</button><button class="primary-button" @click="acceptIdea">That's the one</button></div>
      <small class="integration-note">Curated starter data is included. Add a provider key in the PHP adapter to connect a local places or movie API.</small>
    </div>

    <div v-if="result" class="result-overlay" @click.self="closeResult">
      <div class="result-dialog">
        <button class="dialog-close" @click="closeResult">×</button>
        <span class="eyebrow"><span></span>The wheel chose</span>
        <h2>{{ result }}</h2>
        <p>Notice your first reaction. Relief is an answer. Disappointment is one too.</p>
        <button class="primary-button" @click="acceptResult">Go with {{ result }}</button>
        <button class="soft-button" @click="reconsider">I want to spin again</button>
      </div>
    </div>

    <div v-if="insight" class="result-overlay" @click.self="insight = false">
      <div class="result-dialog insight-dialog">
        <span class="big-quote">“</span>
        <h2>Wanting another spin is information.</h2>
        <p>You may already know what you hoped the wheel would choose. Trust that quiet preference.</p>
        <button class="primary-button" @click="insight = false">I know what I want</button>
        <button class="soft-button" @click="spinAgain">Spin anyway</button>
      </div>
    </div>
  </section>
</template>

<script>
import { api } from '../services/api'

const ideas = [
  { category: 'DINNER', icon: '🍜', title: 'A warm bowl of ramen', detail: 'Comforting, quick, and hard to regret.' },
  { category: 'MOVIE', icon: '🎬', title: 'A 90-minute comedy', detail: 'Light enough to enjoy without homework.' },
  { category: 'BREAK', icon: '🌿', title: 'A twenty-minute walk', detail: 'No destination. Leave your headphones home.' },
  { category: 'LUNCH', icon: '🥗', title: 'The place closest to you', detail: 'Convenience is a perfectly valid criterion.' },
  { category: 'EVENING', icon: '📖', title: 'Read ten pages', detail: 'Small enough to begin. Enough to change pace.' }
]

export default {
  name: 'QuickView',
  inject: ['navigate', 'notify'],
  data: () => ({
    tools: [{ id: 'wheel', icon: '✦', label: 'Decision wheel' }, { id: 'coin', icon: '◐', label: 'Coin flip' }, { id: 'ideas', icon: '▱', label: 'Pick for me' }],
    tool: 'wheel', options: ['Thai food', 'Pizza', 'Sushi', 'Cook at home'],
    colors: ['#163d34', '#b8d9ce', '#eabf8b', '#ecddd0', '#759c91', '#d3c4e3', '#98b8d7', '#e6a29a'],
    spinning: false, rotation: 0, result: '', insight: false,
    coinHeads: 'Order in', coinTails: 'Cook at home', coinFace: 'heads', coinFlipping: false, coinResult: '',
    ideaIndex: 0, cardChanging: false
  }),
  computed: {
    validOptions () { return this.options.filter(Boolean) },
    currentIdea () { return ideas[this.ideaIndex] },
    wheelStyle () {
      const count = Math.max(this.validOptions.length, 1)
      const slices = this.validOptions.map((_, i) => `${this.colors[i % this.colors.length]} ${i * 360 / count}deg ${(i + 1) * 360 / count}deg`)
      return { background: `conic-gradient(${slices.join(',')})`, transform: `rotate(${this.rotation}deg)` }
    }
  },
  methods: {
    labelStyle (index) {
      const angle = (360 / this.validOptions.length) * index + (180 / this.validOptions.length)
      return { transform: `rotate(${angle}deg) translateY(-115px) rotate(${-angle}deg)` }
    },
    spin () {
      if (this.validOptions.length < 2 || this.spinning) return
      this.result = ''
      this.spinning = true
      const index = Math.floor(Math.random() * this.validOptions.length)
      const center = index * (360 / this.validOptions.length) + (180 / this.validOptions.length)
      this.rotation += 1800 + (360 - center) - (this.rotation % 360)
      window.setTimeout(() => { this.spinning = false; this.result = this.validOptions[index] }, 3200)
    },
    closeResult () { this.result = '' },
    reconsider () { this.result = ''; this.insight = true },
    spinAgain () { this.insight = false; this.$nextTick(this.spin) },
    async acceptResult () {
      const choice = this.result
      this.result = ''
      if (localStorage.getItem('calmdecide_token')) {
        try { await api.saveDecision({ title: 'Quick wheel decision', decision_type: 'quick', chosen_option: choice, options: this.validOptions, time_saved: 8 }); this.$emit('saved') } catch (_) { this.notify('Choice made. Sign in again to save it.') }
      }
    },
    flipCoin () {
      if (this.coinFlipping) return
      this.coinFlipping = true; this.coinResult = ''
      window.setTimeout(() => {
        this.coinFace = Math.random() > 0.5 ? 'heads' : 'tails'
        this.coinResult = this.coinFace === 'heads' ? this.coinHeads : this.coinTails
        this.coinFlipping = false
      }, 1000)
    },
    nextIdea () {
      this.cardChanging = true
      window.setTimeout(() => { this.ideaIndex = (this.ideaIndex + 1) % ideas.length; this.cardChanging = false }, 180)
    },
    acceptIdea () { this.notify('Choice made. Now go enjoy it.') }
  }
}
</script>
